/**
 * Smooth language/direction transition utility.
 *
 * Uses GSAP for a premium crossfade: blur + fade out →
 * flip direction → unblur + fade in.
 *
 * Usage:
 *   import { switchLanguage } from '@/utils/langTransition';
 *   await switchLanguage(i18n, 'he');          // one-shot
 *   await switchLanguage(i18n);                // toggle
 *   await switchLanguage(i18n, 'en', true);    // persist to DB
 */

import type { i18n as I18n } from 'i18next';
import { gsap } from 'gsap';
import { http } from '@/network/http';

let transitioning = false;

/**
 * Switch language with a GSAP-powered transition.
 *
 * @param i18n        - i18next instance
 * @param targetLang  - 'he' | 'en' (omit to toggle)
 * @param persist     - if true, PATCH /users/me/language
 */
export async function switchLanguage(
  i18n: I18n,
  targetLang?: string,
  persist = true,
): Promise<void> {
  const newLang = targetLang ?? (i18n.language === 'he' ? 'en' : 'he');
  if (newLang === i18n.language || transitioning) return;

  transitioning = true;
  const newDir = newLang === 'he' ? 'rtl' : 'ltr';
  const root = document.documentElement;

  try {
    // 1. Fade out — blur + opacity on <html> (no scale to avoid fixed-element issues)
    await gsap.to(root, {
      opacity: 0,
      filter: 'blur(6px)',
      duration: 0.25,
      ease: 'power2.in',
    });

    // 2. Flip direction + language while invisible
    root.dir = newDir;
    root.lang = newLang;
    await i18n.changeLanguage(newLang);

    // 3. Allow one frame for layout to settle
    await new Promise((r) => requestAnimationFrame(r));

    // 4. Fade in — unblur + opacity
    await gsap.fromTo(
      root,
      { opacity: 0, filter: 'blur(4px)' },
      {
        opacity: 1,
        filter: 'blur(0px)',
        duration: 0.4,
        ease: 'power2.out',
      },
    );

    // 5. Clean up inline styles
    gsap.set(root, { clearProps: 'opacity,filter' });
  } finally {
    transitioning = false;
  }

  // 6. Persist to DB (fire-and-forget)
  if (persist) {
    http.patch('/users/me/language', { preferredLanguage: newLang }).catch(() => {});
  }
}
